/**
 * <ry-knob>
 *
 * Rotary knob component with drag/wheel interaction.
 *
 * Usage:
 * <ry-knob min="0" max="100" value="50" label="Volume"></ry-knob>
 * <ry-knob min="0" max="3" step="1" labels="Off,Low,Med,High"></ry-knob>
 */

import { RyElement } from '../core/ry-element.js';

export class RyKnob extends RyElement {
  #dragging = false;
  #startY = 0;
  #startValue = 0;
  #ring: HTMLElement | null = null;
  #indicator: HTMLElement | null = null;
  #display: HTMLElement | null = null;

  static observedAttributes = ['min', 'max', 'step', 'value', 'disabled', 'label', 'labels', 'description'] as const;

  setup(): void {
    this.#buildMarkup();
    this.#bindEvents();
    this.#updateVisuals();
  }

  #buildMarkup(): void {
    const label = this.getAttribute('label');
    const description = this.getAttribute('description');
    const titleAttr = description ? ` title="${description}"` : '';

    this.innerHTML = `
      <div data-ry-target="ring" class="ry-knob__ring"${titleAttr}>
        <div data-ry-target="cap" class="ry-knob__cap">
          <div data-ry-target="indicator" class="ry-knob__indicator"></div>
        </div>
      </div>
      <span data-ry-target="display" class="ry-knob__value"></span>
      ${label ? `<span data-ry-target="label" class="ry-knob__label"${titleAttr}>${label}</span>` : ''}
    `;

    this.#ring = this.$('[data-ry-target="ring"]');
    this.#indicator = this.$('[data-ry-target="indicator"]');
    this.#display = this.$('[data-ry-target="display"]');
  }

  #bindEvents(): void {
    if (!this.#ring) return;

    // Mouse drag
    this.on(this.#ring, 'mousedown', this.#handleMouseDown);
    this.on(document, 'mousemove', this.#handleMouseMove);
    this.on(document, 'mouseup', this.#handleMouseUp);

    // Touch support
    this.on(this.#ring, 'touchstart', this.#handleTouchStart);
    this.on(document, 'touchmove', this.#handleTouchMove);
    this.on(document, 'touchend', this.#handleTouchEnd);

    // Wheel
    this.on(this.#ring, 'wheel', this.#handleWheel);

    // Keyboard
    this.on(this, 'keydown', this.#handleKeydown);
  }

  #handleMouseDown = (e: MouseEvent): void => {
    if (this.disabled) return;
    e.preventDefault();
    this.#dragging = true;
    this.#startY = e.clientY;
    this.#startValue = this.value;
    this.#ring?.classList.add('ry-knob__ring--dragging');
    this.setAttribute('data-dragging', '');
  };

  #handleMouseMove = (e: MouseEvent): void => {
    if (!this.#dragging) return;
    const deltaY = this.#startY - e.clientY;
    const sensitivity = (this.max - this.min) / 100;
    this.#updateValue(this.#startValue + deltaY * sensitivity);
  };

  #handleMouseUp = (): void => {
    if (this.#dragging) {
      this.#dragging = false;
      this.#ring?.classList.remove('ry-knob__ring--dragging');
      this.removeAttribute('data-dragging');
      this.#emitChange();
    }
  };

  #handleTouchStart = (e: TouchEvent): void => {
    if (this.disabled) return;
    e.preventDefault();
    this.#dragging = true;
    this.#startY = e.touches[0]?.clientY ?? 0;
    this.#startValue = this.value;
    this.#ring?.classList.add('ry-knob__ring--dragging');
    this.setAttribute('data-dragging', '');
  };

  #handleTouchMove = (e: TouchEvent): void => {
    if (!this.#dragging) return;
    e.preventDefault();
    const touch = e.touches[0];
    if (!touch) return;
    const deltaY = this.#startY - touch.clientY;
    const sensitivity = (this.max - this.min) / 100;
    this.#updateValue(this.#startValue + deltaY * sensitivity);
  };

  #handleTouchEnd = (): void => {
    if (this.#dragging) {
      this.#dragging = false;
      this.#ring?.classList.remove('ry-knob__ring--dragging');
      this.removeAttribute('data-dragging');
      this.#emitChange();
    }
  };

  #handleWheel = (e: WheelEvent): void => {
    if (this.disabled) return;
    e.preventDefault();
    const step = this.step || (this.max - this.min) / 50;
    const delta = e.deltaY < 0 ? step : -step;
    this.#updateValue(this.value + delta);
    this.#emitChange();
  };

  #handleKeydown = (e: KeyboardEvent): void => {
    if (this.disabled) return;

    const step = this.step || 1;
    const largeStep = (this.max - this.min) / 10;
    let delta = 0;

    switch (e.key) {
      case 'ArrowUp':
      case 'ArrowRight':
        delta = step;
        break;
      case 'ArrowDown':
      case 'ArrowLeft':
        delta = -step;
        break;
      case 'PageUp':
        delta = largeStep;
        break;
      case 'PageDown':
        delta = -largeStep;
        break;
      case 'Home':
        this.#updateValue(this.min);
        this.#emitChange();
        return;
      case 'End':
        this.#updateValue(this.max);
        this.#emitChange();
        return;
      default:
        return;
    }

    e.preventDefault();
    this.#updateValue(this.value + delta);
    this.#emitChange();
  };

  #updateValue(newValue: number): void {
    let clamped = Math.max(this.min, Math.min(this.max, newValue));

    // Snap to step if defined
    const step = this.step;
    if (step > 0) {
      clamped = Math.round((clamped - this.min) / step) * step + this.min;
      clamped = Math.max(this.min, Math.min(this.max, clamped));
    }

    this.value = clamped;
    this.#updateVisuals();
    this.#emitInput();
  }

  #updateVisuals(): void {
    const percent = (this.value - this.min) / (this.max - this.min);
    // Rotation: -135deg (min) to +135deg (max) = 270deg range
    const rotation = -135 + percent * 270;

    if (this.#ring) {
      this.#ring.style.setProperty('--knob-rotation', `${rotation}deg`);
      this.#ring.style.setProperty('--knob-percent', String(percent));
    }

    if (this.#display) {
      this.#display.textContent = this.#formatValue(this.value);
    }

    // Update ARIA
    this.setAttribute('aria-valuenow', String(this.value));
    this.setAttribute('aria-valuemin', String(this.min));
    this.setAttribute('aria-valuemax', String(this.max));
  }

  #formatValue(value: number): string {
    const labelsAttr = this.getAttribute('labels');
    if (labelsAttr) {
      const labels = labelsAttr.split(',');
      const index = Math.round(value);
      return labels[index] ?? String(index);
    }

    const step = this.step;
    if (step >= 1) return Math.round(value).toString();
    if (Math.abs(value) >= 1000) return (value / 1000).toFixed(1) + 'k';
    if (Math.abs(value) >= 100) return Math.round(value).toString();
    if (Math.abs(value) >= 10) return value.toFixed(1);
    return value.toFixed(2);
  }

  #emitInput(): void {
    const percent = (this.value - this.min) / (this.max - this.min);
    this.emit('input', { value: this.value, percent });
  }

  #emitChange(): void {
    const percent = (this.value - this.min) / (this.max - this.min);
    this.emit('change', { value: this.value, percent });
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (name === 'value' && this.#ring) {
      this.#updateVisuals();
    }
  }

  // --- Public API ---

  get min(): number {
    return parseFloat(this.getAttribute('min') ?? '0');
  }
  set min(val: number) {
    this.setAttribute('min', String(val));
  }

  get max(): number {
    return parseFloat(this.getAttribute('max') ?? '100');
  }
  set max(val: number) {
    this.setAttribute('max', String(val));
  }

  get step(): number {
    return parseFloat(this.getAttribute('step') ?? '0');
  }
  set step(val: number) {
    this.setAttribute('step', String(val));
  }

  get value(): number {
    return parseFloat(this.getAttribute('value') ?? String(this.min));
  }
  set value(val: number) {
    this.setAttribute('value', String(val));
  }

  get disabled(): boolean {
    return this.hasAttribute('disabled');
  }
  set disabled(val: boolean) {
    if (val) {
      this.setAttribute('disabled', '');
    } else {
      this.removeAttribute('disabled');
    }
  }
}

customElements.define('ry-knob', RyKnob);
